import { apiRequest } from '@/lib/api-client';

export interface ScoreBreakdown {
  preference: number;
  grade_appropriateness: number;
  skill_gap: number;
  novelty: number;
  engagement: number;
}

export interface RecommendedTheme {
  theme_id: string;
  theme_slug: string;
  theme_label: string;
  emoji?: string | null;
  score: number;
  score_breakdown: ScoreBreakdown;
}

export interface RecommendedStory {
  story_id: string;
  title: string;
  theme_id: string;
  theme_slug: string;
  theme_label: string;
  word_count?: number | null;
  recommendation_score: number;
  reason: string;
}

export interface GenerationRequirement {
  theme_id: string;
  theme_slug: string;
  theme_label: string;
  prompt_text: string;
  grade_level: number;
  target_focus_types: string[];
  cognitive_complexity: string;
  emotional_complexity: string;
  target_word_count_range: [number, number] | null;
  recommendation_score: number;
  reason: string;
}

export interface RecommendationResponse {
  student_id: string;
  grade_level?: number | null;
  is_cold_start: boolean;
  recommended_stories: RecommendedStory[];
  recommended_themes: RecommendedTheme[];
  generation_requirements: GenerationRequirement[];
}

export interface QuickRecommendationResponse {
  type: 'existing_story' | 'generate_new' | 'no_recommendation';
  story_id?: string | null;
  title?: string | null;
  theme_id?: string | null;
  theme_slug?: string | null;
  theme_label?: string | null;
  reason: string;
}

export interface ThemeRecommendationResponse {
  student_id: string;
  grade_level?: number | null;
  themes: RecommendedTheme[];
}

export const recommendationsApi = {
  async getRecommendations(params?: {
    limit?: number;
    include_generation_requirements?: boolean;
  }): Promise<RecommendationResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.include_generation_requirements !== undefined) {
      queryParams.append('include_generation_requirements', params.include_generation_requirements.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/recommendations?${queryString}` : '/recommendations';

    return apiRequest<RecommendationResponse>(url, {}, 'Failed to fetch recommendations');
  },

  async getQuickRecommendation(): Promise<QuickRecommendationResponse> {
    return apiRequest<QuickRecommendationResponse>(
      '/recommendations/quick',
      {},
      'Failed to fetch quick recommendation'
    );
  },

  async getThemeRecommendations(limit?: number): Promise<ThemeRecommendationResponse> {
    const url = limit ? `/recommendations/themes?limit=${limit}` : '/recommendations/themes';
    return apiRequest<ThemeRecommendationResponse>(url, {}, 'Failed to fetch theme recommendations');
  },

  async getQuickRecommendationForStudent(studentId: string): Promise<QuickRecommendationResponse> {
    return apiRequest<QuickRecommendationResponse>(
      `/students/${studentId}/recommendations/quick`,
      {},
      'Failed to fetch recommendation for student'
    );
  },
};
